/**
 * @description
 * Main dashboard page displaying overview metrics, active clients,
 * and recent deployments.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 * @see admin/src/frontend/pages/Dashboard.test.tsx
 */

import { Link } from 'react-router';
import { useClients } from '../hooks/useClients';

/**
 * Dashboard page component
 */
export function Dashboard() {
  const { clients, loading: clientsLoading } = useClients();

  const loading = clientsLoading;

  // Calculate stats from real data
  const stats = {
    totalClients: clients.length,
    activeClients: clients.filter(c => c.status === 'active').length,
    totalDeployments: 0, // Will be shown only on deployments page
    activeDeployments: 0, // Will be shown only on deployments page
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Overview of your platform and client deployments
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border bg-card p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-muted-foreground">Total Clients</p>
            <svg
              className="h-4 w-4 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <div className="mt-2">
            <p className="text-3xl font-bold">{stats.totalClients}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.activeClients} active
            </p>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-muted-foreground">Active Clients</p>
            <svg
              className="h-4 w-4 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="mt-2">
            <p className="text-3xl font-bold">{stats.activeClients}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {Math.round((stats.activeClients / stats.totalClients) * 100)}% uptime
            </p>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-muted-foreground">Total Deployments</p>
            <svg
              className="h-4 w-4 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>
          <div className="mt-2">
            <p className="text-3xl font-bold">{stats.totalDeployments}</p>
            <p className="text-xs text-muted-foreground mt-1">All time</p>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-muted-foreground">Active Deployments</p>
            <svg
              className="h-4 w-4 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <div className="mt-2">
            <p className="text-3xl font-bold">{stats.activeDeployments}</p>
            <p className="text-xs text-muted-foreground mt-1">In progress</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border bg-card p-6">
          <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
          <div className="space-y-2">
            <Link
              to="/clients"
              className="flex items-center justify-between rounded-md border p-3 transition-colors hover:bg-accent"
            >
              <span className="font-medium">View All Clients</span>
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
            <Link
              to="/deployments"
              className="flex items-center justify-between rounded-md border p-3 transition-colors hover:bg-accent"
            >
              <span className="font-medium">View Deployments</span>
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
            <Link
              to="/settings"
              className="flex items-center justify-between rounded-md border p-3 transition-colors hover:bg-accent"
            >
              <span className="font-medium">System Settings</span>
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6">
          <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {clients.slice(0, 3).map((client) => (
              <div key={client.id} className="flex items-start gap-3">
                <div className={`mt-1 h-2 w-2 rounded-full ${
                  client.status === 'active' ? 'bg-green-600' :
                  client.status === 'suspended' ? 'bg-red-600' :
                  'bg-yellow-600'
                }`}></div>
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {client.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {client.subdomain}.byteable.ai • {client.status}
                  </p>
                </div>
              </div>
            ))}
            {clients.length === 0 && (
              <p className="text-sm text-muted-foreground">No clients yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */